import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Divider,
  Link as ChakraLink
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const MphReviewFeedback = () => {
  const { accessToken } = useContext(AppContext);
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!accessToken) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await callApi('/api/mph_sa/feedback', accessToken);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            setCases(data.data || []);
          } else {
            throw new Error(data.message || 'Failed to fetch feedback');
          }
        } else {
          throw new Error('Failed to fetch feedback');
        }
      } catch (err) {
        console.error('Error fetching MPH feedback:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeedback();
  }, [accessToken]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box h="100vh" display="flex" flexDirection="column">
      <HeaderMenus />
      
      <Box p={8} flex="1" overflowY="auto" bg="gray.50">
        <Box maxW="1000px" mx="auto">
          <HStack justifyContent="space-between" mb={6}>
            <Heading size="lg" color="gray.800">MPH Reviewer Feedback</Heading>
            <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
              {cases.length} Cases Audited
            </Badge>
          </HStack>

          {error && (
            <Box bg="red.100" color="red.700" p={4} borderRadius="md" mb={6}>
              {error}
            </Box>
          )}

          {isLoading ? (
            <VStack py={10}>
              <Spinner size="xl" color="blue.500" />
              <Text color="gray.500">Loading feedback...</Text>
            </VStack>
          ) : cases.length === 0 ? (
            <Box bg="white" p={8} borderRadius="md" shadow="sm" textAlign="center">
              <Text color="gray.500">No feedback found for MPH Surgical Assist cases.</Text>
            </Box>
          ) : (
            <VStack spacing={6} align="stretch">
              {cases.map((caseItem) => (
                <Card key={caseItem.external_case_id} shadow="sm" borderWidth="1px">
                  <CardHeader bg="gray.50" borderBottomWidth="1px" py={3}>
                    <HStack justify="space-between">
                      <HStack>
                        <Text fontWeight="bold" color="gray.600">Case ID:</Text>
                        <ChakraLink 
                          as={RouterLink} 
                          to={`/mph-surgical-assist/${caseItem.external_case_id}`}
                          color="blue.600"
                          fontWeight="bold"
                          isExternal
                        >
                          {caseItem.external_case_id}
                        </ChakraLink>
                      </HStack>
                      <Text fontSize="sm" color="gray.500">
                        Created: {formatDate(caseItem.case_created_at)}
                      </Text>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing={4}>
                      {caseItem.feedback.map((fb, index) => (
                        <Box key={index}>
                          {index > 0 && <Divider mb={4} />}
                          <HStack justify="space-between" mb={2}>
                            <Badge colorScheme="purple" variant="subtle">
                              {fb.reviewer_email || 'Unknown Reviewer'}
                            </Badge>
                            <Text fontSize="xs" color="gray.500">
                              {formatDate(fb.created_at)}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap" pl={2} borderLeftWidth="3px" borderColor="purple.200">
                            {fb.notes}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MphReviewFeedback;